import React, { memo, useMemo } from 'react';
import { Box, SpaceBetween } from '@cloudscape-design/components';

export type ReviewTextProps = {
  text?: string | null;
  maxCharacters?: number;
  highlights?: string[];
  highlightCaseSensitive?: boolean;
};

export function ReviewText(props: ReviewTextProps) {
  const { text, maxCharacters, highlights, highlightCaseSensitive } = props;

  const adjustedHighlights = useMemo(() => {
    if (highlightCaseSensitive) {
      return highlights;
    }
    return highlights?.map((highlight) => highlight.toLowerCase());
  }, [highlights, highlightCaseSensitive]);

  const formattedText = useMemo(() => {
    let formattedText = text ?? '';
    if (maxCharacters && formattedText.length > maxCharacters) {
      // formattedText = formattedText.substring(0, maxCharacters) + '...';
      // split words by whitespace, then join until maxCharacters is reached
      const words = formattedText.split(' ');
      let truncatedText = '';
      for (const word of words) {
        if ((truncatedText + word).length > maxCharacters) {
          break;
        }
        truncatedText += word + ' ';
      }
      formattedText = truncatedText.trim();
      return formattedText + '...';
    }
    return formattedText;
  }, [text, maxCharacters]);

  const getHighlightIndices = (line: string) => {
    const indices: { start: number; end: number }[] = [];
    if (adjustedHighlights) {
      for (const highlight of adjustedHighlights) {
        if (!highlight) {
          continue;
        }
        let startIndex = line.indexOf(highlight);
        while (startIndex !== -1) {
          indices.push({ start: startIndex, end: startIndex + highlight.length });
          startIndex = line.indexOf(highlight, startIndex + 1);
        }
      }
    }
    return indices;
  };

  const renderTextWithHighlights = (line: string) => {
    const highlightIndices = getHighlightIndices(
      highlightCaseSensitive ? line : line.toLowerCase(),
    );
    const parts = [];
    let lastIndex = 0;

    highlightIndices.sort((a, b) => a.start - b.start);

    for (const { start, end } of highlightIndices) {
      if (lastIndex < start) {
        parts.push(<span key={lastIndex}>{line.substring(lastIndex, start)}</span>);
      }
      parts.push(
        <span className="highlight" key={start}>
          {line.substring(start, end)}
        </span>,
      );
      lastIndex = end;
    }

    if (lastIndex < line.length) {
      parts.push(<span key={lastIndex}>{line.substring(lastIndex)}</span>);
    }

    return parts;
  };

  const MemoizedLine = memo(({ line }: { line: string }) => (
    <Box variant="p">{renderTextWithHighlights(line)}</Box>
  ));

  return (
    <SpaceBetween direction="vertical" size="xs">
      {formattedText.split('\n').map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <MemoizedLine key={`${index}-${line}`} line={line} />
      ))}
    </SpaceBetween>
  );
}
